import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Camera, Download, Loader2, X, Music } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API}/api`;

export default function EventGalleryPage() {
  const { eventCode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, [eventCode]);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API_BASE}/event/${eventCode}/gallery`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gallery not found');
    } finally {
      setLoading(false);
    }
  };

  const downloadPhoto = (photoUrl, name) => {
    const url = photoUrl.startsWith('/') ? `${API}${photoUrl}` : photoUrl;
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'photo.jpg';
    a.target = '_blank';
    a.click();
  };

  const pc = data?.primary_color || '#e91e63';
  const bgType = data?.background_type || 'gradient';
  const bgVal = data?.background_value || '#1a1a2e,#16213e';
  const fontClass = data?.font_style === 'modern' ? 'font-sans' : data?.font_style === 'fun' ? 'font-mono' : 'font-serif';

  const bgStyle = bgType === 'gradient'
    ? { background: `linear-gradient(135deg, ${bgVal.split(',')[0]}, ${bgVal.split(',')[1] || bgVal.split(',')[0]})` }
    : { background: bgVal || '#0f0f1a' };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <Loader2 className="w-8 h-8 animate-spin text-white/50" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="text-center">
        <Camera className="w-16 h-16 text-white/20 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Gallery Not Found</h1>
        <p className="text-white/50">{error}</p>
      </div>
    </div>
  );

  const allPhotos = [
    ...(data.featured_photos || []).map(p => ({ ...p, isFeatured: true })),
    ...(data.photos || []).map(p => ({ ...p, isFeatured: false }))
  ];

  return (
    <div className={`min-h-screen ${fontClass} pb-12`} style={bgStyle} data-testid="event-gallery">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 text-center">
          <div className="flex items-center justify-center gap-3">
            {data.couple_photo_url && (
              <div className="w-10 h-10 rounded-full overflow-hidden border-2" style={{ borderColor: pc }}>
                <img src={data.couple_photo_url.startsWith('/') ? `${API}${data.couple_photo_url}` : data.couple_photo_url} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-white">{data.event_title}</h1>
              <p className="text-white/40 text-xs">{allPhotos.length} photos</p>
            </div>
          </div>
        </div>
      </header>

      {/* Photo Grid */}
      <div className="max-w-4xl mx-auto px-3 py-4">
        {allPhotos.length === 0 ? (
          <div className="text-center py-20">
            <Camera className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-lg">No photos yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="gallery-grid">
            {allPhotos.map((photo, i) => (
              <button
                key={photo.id || i}
                onClick={() => setViewingPhoto(photo)}
                className="aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/5 hover:border-white/20 transition-all relative group"
                data-testid={`gallery-photo-${i}`}
              >
                <img
                  src={(photo.photo_url || '').startsWith('/') ? `${API}${photo.photo_url}` : photo.photo_url}
                  alt={photo.caption || ''}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {photo.isFeatured && (
                  <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-black/60 rounded-md">
                    <span className="text-[10px] text-white/70">Featured</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <Download className="w-6 h-6 text-white" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Photo Lightbox */}
      {viewingPhoto && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setViewingPhoto(null)}>
          <button className="absolute top-4 right-4 p-2 bg-white/10 rounded-full z-10" onClick={() => setViewingPhoto(null)}>
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={(viewingPhoto.photo_url || '').startsWith('/') ? `${API}${viewingPhoto.photo_url}` : viewingPhoto.photo_url}
            alt=""
            className="max-w-full max-h-[80vh] rounded-lg"
            onClick={e => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-0 right-0 text-center space-y-3">
            {viewingPhoto.guest_name && (
              <p className="text-white/60 text-sm">Photo by {viewingPhoto.guest_name}</p>
            )}
            <Button
              onClick={(e) => { e.stopPropagation(); downloadPhoto(viewingPhoto.photo_url, `${viewingPhoto.guest_name || 'photo'}.jpg`); }}
              className="rounded-xl"
              style={{ background: pc }}
              data-testid="download-single-photo-btn"
            >
              <Download className="w-4 h-4 mr-2" />Download Photo
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
